import { z } from 'zod';
import { ExecutionMetricsSchema, QualityScoreDimensionsSchema, type ExecutionMetrics } from './execution-intelligence';

/**
 * Requirement #11 — CI/CD Quality Gates Foundation.
 * Requirement #12 — CI Quality Gate Emission (Maintain) consumes these
 * verdicts via CiRunSummary + provider-agnostic artifacts.
 *
 * Deterministic gate evaluation for:
 *   A. Quality-first  (scenario / execution — optional requirements)
 *   B. Automation-first (flow / run / suite — no Scenario required)
 *   C. Linked Quality ↔ Automation when relationships exist
 *
 * Consumes Requirement #10 intelligence + existing test_runs.
 * Does NOT introduce a second automation engine.
 */

export const QUALITY_GATE_VERDICTS = ['pass', 'warn', 'fail'] as const;
export type QualityGateVerdict = (typeof QUALITY_GATE_VERDICTS)[number];

export const QUALITY_GATE_SCOPES = ['run', 'flow', 'scenario', 'execution', 'suite'] as const;
export type QualityGateScope = (typeof QUALITY_GATE_SCOPES)[number];

export const QualityGateThresholdsSchema = z.object({
  /** Minimum pass rate % (0–100). Null = not enforced. */
  minPassRate: z.number().min(0).max(100).nullable().default(90),
  /** Maximum allowed failed executions/runs in the window. Null = not enforced. */
  maxFailures: z.number().int().nonnegative().nullable().default(0),
  /** Maximum healing rate % before warn/fail. Null = not enforced. */
  maxHealingRate: z.number().min(0).max(100).nullable().default(25),
  /** Maximum retry/unstable rate % before warn. Null = not enforced. */
  maxUnstableRate: z.number().min(0).max(100).nullable().default(20),
  /** Require at least this many completed samples. */
  minSampleSize: z.number().int().positive().default(1),
  /** Lookback window in days for flow/scenario aggregate gates. */
  lookbackDays: z.number().int().positive().max(90).default(14),
});
export type QualityGateThresholds = z.infer<typeof QualityGateThresholdsSchema>;

export const DEFAULT_QUALITY_GATE_THRESHOLDS: QualityGateThresholds = {
  minPassRate: 90,
  maxFailures: 0,
  maxHealingRate: 25,
  maxUnstableRate: 20,
  minSampleSize: 1,
  lookbackDays: 14,
};

export const QualityGateEvaluateRequestSchema = z
  .object({
    /** Automation-first: evaluate a single test_runs row. */
    runId: z.string().uuid().optional(),
    /** Automation-first: aggregate recent runs for a flow. */
    flowId: z.string().uuid().optional(),
    /** Quality-first: aggregate scenario executions. */
    scenarioId: z.string().uuid().optional(),
    /** Quality-first: single quality_scenario_executions row. */
    executionId: z.string().uuid().optional(),
    thresholds: QualityGateThresholdsSchema.partial().optional(),
  })
  .refine((v) => Boolean(v.runId || v.flowId || v.scenarioId || v.executionId), {
    message: 'One of runId, flowId, scenarioId, or executionId is required',
  });
export type QualityGateEvaluateRequest = z.infer<typeof QualityGateEvaluateRequestSchema>;

export const QualityGateReasonSchema = z.object({
  code: z.string(),
  severity: z.enum(QUALITY_GATE_VERDICTS),
  message: z.string(),
  actual: z.number().nullable().optional(),
  threshold: z.number().nullable().optional(),
});
export type QualityGateReason = z.infer<typeof QualityGateReasonSchema>;

export const QualityGateTraceabilitySchema = z.object({
  requirementIds: z.array(z.string().uuid()),
  scenarioId: z.string().uuid().nullable(),
  automationFlowId: z.string().uuid().nullable(),
  executionId: z.string().uuid().nullable(),
  runId: z.string().uuid().nullable(),
});
export type QualityGateTraceability = z.infer<typeof QualityGateTraceabilitySchema>;

export const QualityGateResultSchema = z.object({
  verdict: z.enum(QUALITY_GATE_VERDICTS),
  /** CI exit hint: 0 = pass, 1 = fail, 2 = warn (non-blocking if CI treats warn as soft). */
  exitCode: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  scope: z.enum(QUALITY_GATE_SCOPES),
  sampleSize: z.number().int().nonnegative(),
  passRate: z.number().min(0).max(100).nullable(),
  failureCount: z.number().int().nonnegative(),
  healingRate: z.number().min(0).max(100).nullable(),
  unstableRate: z.number().min(0).max(100).nullable(),
  thresholds: QualityGateThresholdsSchema,
  reasons: z.array(QualityGateReasonSchema),
  metrics: ExecutionMetricsSchema.nullable(),
  qualityScore: QualityScoreDimensionsSchema.optional(),
  traceability: QualityGateTraceabilitySchema,
  /** Engine path when known — proves gates sit on the shared mvp path. */
  enginePath: z.literal('step-processor').optional(),
  evaluatedAt: z.string(),
});
export type QualityGateResult = z.infer<typeof QualityGateResultSchema>;

export const QualityGateEvaluateResponseSchema = z.object({
  gate: QualityGateResultSchema,
});
export type QualityGateEvaluateResponse = z.infer<typeof QualityGateEvaluateResponseSchema>;

/** Sparse gate fields attached to CiRunSummary (#12 emission). */
export const CiQualityGateSummarySchema = z.object({
  verdict: z.enum(QUALITY_GATE_VERDICTS),
  exitCode: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  scope: z.enum(QUALITY_GATE_SCOPES),
  sampleSize: z.number().int().nonnegative(),
  passRate: z.number().min(0).max(100).nullable(),
  failureCount: z.number().int().nonnegative(),
  healingRate: z.number().min(0).max(100).nullable(),
  unstableRate: z.number().min(0).max(100).nullable(),
  reasons: z.array(QualityGateReasonSchema).max(20),
  enginePath: z.literal('step-processor').optional(),
  evaluatedAt: z.string(),
});
export type CiQualityGateSummary = z.infer<typeof CiQualityGateSummarySchema>;

export const QualityGateCiSummaryResponseSchema = z.object({
  gate: QualityGateResultSchema,
  /** Merge into CiRunSummary.qualityGate before dispatchCiEmit. */
  ciQualityGate: CiQualityGateSummarySchema,
  /** Recommended process exit code for CI (same as gate.exitCode). */
  exitCode: z.union([z.literal(0), z.literal(1), z.literal(2)]),
});
export type QualityGateCiSummaryResponse = z.infer<typeof QualityGateCiSummaryResponseSchema>;

export function qualityGateExitCode(verdict: QualityGateVerdict): 0 | 1 | 2 {
  if (verdict === 'pass') return 0;
  if (verdict === 'warn') return 2;
  return 1;
}

function worseVerdict(a: QualityGateVerdict, b: QualityGateVerdict): QualityGateVerdict {
  const rank = { pass: 0, warn: 1, fail: 2 } as const;
  return rank[b] > rank[a] ? b : a;
}

/**
 * Pure threshold evaluator — shared by API (#11) and CLI (#12).
 * Does not touch Playwright, DB, or providers.
 */
export function evaluateGateAgainstMetrics(opts: {
  sampleSize: number;
  passRate: number | null;
  failureCount: number;
  healingRate: number | null;
  unstableRate: number | null;
  thresholds: QualityGateThresholds;
  metrics?: ExecutionMetrics | null;
}): { verdict: QualityGateVerdict; reasons: QualityGateReason[] } {
  const { thresholds } = opts;
  const reasons: QualityGateReason[] = [];
  let verdict: QualityGateVerdict = 'pass';

  if (opts.sampleSize < thresholds.minSampleSize) {
    reasons.push({
      code: 'insufficient_sample',
      severity: 'fail',
      message: `Need at least ${thresholds.minSampleSize} completed sample(s); got ${opts.sampleSize}`,
      actual: opts.sampleSize,
      threshold: thresholds.minSampleSize,
    });
    return { verdict: 'fail', reasons };
  }

  if (thresholds.maxFailures != null && opts.failureCount > thresholds.maxFailures) {
    reasons.push({
      code: 'max_failures',
      severity: 'fail',
      message: `Failure count ${opts.failureCount} exceeds max ${thresholds.maxFailures}`,
      actual: opts.failureCount,
      threshold: thresholds.maxFailures,
    });
    verdict = worseVerdict(verdict, 'fail');
  }

  if (thresholds.minPassRate != null && opts.passRate != null && opts.passRate < thresholds.minPassRate) {
    reasons.push({
      code: 'min_pass_rate',
      severity: 'fail',
      message: `Pass rate ${opts.passRate}% is below minimum ${thresholds.minPassRate}%`,
      actual: opts.passRate,
      threshold: thresholds.minPassRate,
    });
    verdict = worseVerdict(verdict, 'fail');
  }

  if (thresholds.maxHealingRate != null && opts.healingRate != null && opts.healingRate > thresholds.maxHealingRate) {
    const severity: QualityGateVerdict = opts.healingRate >= thresholds.maxHealingRate * 1.5 ? 'fail' : 'warn';
    reasons.push({
      code: 'max_healing_rate',
      severity,
      message: `Healing rate ${opts.healingRate}% exceeds threshold ${thresholds.maxHealingRate}%`,
      actual: opts.healingRate,
      threshold: thresholds.maxHealingRate,
    });
    verdict = worseVerdict(verdict, severity);
  }

  if (
    thresholds.maxUnstableRate != null &&
    opts.unstableRate != null &&
    opts.unstableRate > thresholds.maxUnstableRate
  ) {
    reasons.push({
      code: 'max_unstable_rate',
      severity: 'warn',
      message: `Unstable rate ${opts.unstableRate}% exceeds threshold ${thresholds.maxUnstableRate}%`,
      actual: opts.unstableRate,
      threshold: thresholds.maxUnstableRate,
    });
    verdict = worseVerdict(verdict, 'warn');
  }

  if (reasons.length === 0) {
    reasons.push({
      code: 'all_clear',
      severity: 'pass',
      message: 'All quality gate thresholds satisfied',
    });
  }

  return { verdict, reasons };
}

export function toCiQualityGateSummary(gate: QualityGateResult): CiQualityGateSummary {
  return {
    verdict: gate.verdict,
    exitCode: gate.exitCode,
    scope: gate.scope,
    sampleSize: gate.sampleSize,
    passRate: gate.passRate,
    failureCount: gate.failureCount,
    healingRate: gate.healingRate,
    unstableRate: gate.unstableRate,
    reasons: gate.reasons.slice(0, 20),
    enginePath: gate.enginePath,
    evaluatedAt: gate.evaluatedAt,
  };
}

/** Filename written by dispatchCiEmit for every provider (#12). */
export const QUALITY_GATE_CI_ARTIFACT_NAME = 'rynora-quality-gate.json';
